import React from 'react';
import classNames from 'classnames';

// Import util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import {
  EXTENDED_DATA_SCHEMA_TYPES,
  LISTING_STATE_DRAFT,
  SCHEMA_TYPE_ENUM,
  // SCHEMA_TYPE_MULTI_ENUM,
} from '../../../../util/types';
import {
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
} from '../../../../util/fieldHelpers';
import { isBookingProcessAlias } from '../../../../transactions/transaction';

// Import shared components
import { H3, ListingLink } from '../../../../components';

// Import modules from this directory
import ErrorMessage from './ErrorMessage';
import EditListingDetailsForm from './EditListingDetailsForm';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import css from './EditListingDetailsPanel.module.css';
import { BOOKING_PROCESSES, MOORING_BUOY } from '../../../../util/enums';
import { convertGeolocationToParams } from '../../../../util/genericHelpers';
import { isPublicMoorageListing, PUBLIC_MOORAGE_CATEGORY } from '../../../../util/listingHelpers';
import { normalizeHarbormasterWebsite } from '../../../../util/publicMoorageActions';

const { Money, LatLng } = sdkTypes;
const SET_PRICE_FOR_BUOY = 3000;
const SET_PRICE_FOR_DOCK = 5000;

const omitCategoryById = (categories = [], categoryId) =>
  categories
    .filter(category => category.id !== categoryId)
    .map(category => ({
      ...category,
      subcategories: omitCategoryById(category.subcategories || [], categoryId),
    }));
/**
 * Get listing configuration. For existing listings, it is stored to publicData.
 * For new listings, the data needs to be figured out from listingTypes configuration.
 *
 * In the latter case, we select first type in the array. However, EditListingDetailsForm component
 * gets 'selectableListingTypes' prop, which it uses to provide a way to make selection,
 * if multiple listing types are available.
 *
 * @param {Array} listingTypes
 * @param {Object} existingListingTypeInfo
 * @returns an object containing mainly information that can be stored to publicData.
 */
const getTransactionInfo = (listingTypes, existingListingTypeInfo = {}, inlcudeLabel = false) => {
  const { listingType, transactionProcessAlias, unitType } = existingListingTypeInfo;

  if (listingType && transactionProcessAlias && unitType) {
    return { listingType, transactionProcessAlias, unitType };
  } else if (listingTypes.length === 1) {
    const { listingType: type, label, transactionType } = listingTypes[0];
    const { alias, unitType: configUnitType } = transactionType;
    const labelMaybe = inlcudeLabel ? { label: label || type } : {};
    return {
      listingType: type,
      transactionProcessAlias: alias,
      unitType: configUnitType,
      ...labelMaybe,
    };
  }
  return {};
};

/**
 * Check if listingType has already been set.
 *
 * If listing type (incl. process & unitType) has been set, we won't allow change to it.
 * It's possible to make it editable, but it becomes somewhat complex to modify following panels,
 * for the different process. (E.g. adjusting stock vs booking availability settings,
 * if process has been changed for existing listing.)
 *
 * @param {Object} publicData JSON-like data stored to listing entity.
 * @returns object literal with to keys: { hasExistingListingType, existingListingTypeInfo }
 */
const hasSetListingType = publicData => {
  const { listingType, transactionProcessAlias, unitType } = publicData;
  const existingListingTypeInfo = { listingType, transactionProcessAlias, unitType };

  return {
    hasExistingListingType: !!listingType && !!transactionProcessAlias && !!unitType,
    existingListingTypeInfo,
  };
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the listing and target scope and listing type.
 *
 * This expects submit data to be namespaced (e.g. 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target listing type.
 *       These might exists if provider swaps between listing types before saving the draft listing.
 *
 * @param {Object} data values to look through against listingConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetListingType Check that the extended data is relevant for this listing type.
 * @param {Object} listingFieldConfigs an extended data configurtions for listing fields.
 * @returns Array of picked extended data fields from submitted data.
 */
const pickListingFieldsData = (
  data,
  targetScope,
  targetListingType,
  targetCategories,
  listingFieldConfigs
) => {
  const targetCategoryIds = Object.values(targetCategories);

  return listingFieldConfigs.reduce((fields, fieldConfig) => {
    const { key, scope = 'public', schemaType } = fieldConfig || {};
    const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
    const namespacedKey = `${namespacePrefix}${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetListingType = isFieldForListingType(targetListingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(targetCategoryIds, fieldConfig);

    if (isKnownSchemaType && isTargetScope && isTargetListingType && isTargetCategory) {
      const fieldValue = data[namespacedKey] || null;
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope) {
      // Note: this clears extra custom fields
      // These might exists if provider swaps between listing types before saving the draft listing.
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};

/**
 * Pick extended data fields from given extended data of the listing entity.
 * Picking is based on extended data configuration for the listing and target scope and listing type.
 *
 * This returns namespaced (e.g. 'pub_') initial values for the form.
 *
 * @param {Object} data extended data values to look through against listingConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetListingType Check that the extended data is relevant for this listing type.
 * @param {Object} listingFieldConfigs an extended data configurtions for listing fields.
 * @returns Array of picked extended data fields
 */
const initialValuesForListingFields = (
  data,
  targetScope,
  targetListingType,
  targetCategories,
  listingFieldConfigs
) => {
  const targetCategoryIds = Object.values(targetCategories);

  return listingFieldConfigs.reduce((fields, fieldConfig) => {
    const { key, scope = 'public', schemaType, enumOptions } = fieldConfig || {};
    const namespacePrefix = scope === 'public' ? `pub_` : `priv_`;
    const namespacedKey = `${namespacePrefix}${key}`;

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isEnumSchemaType = schemaType === SCHEMA_TYPE_ENUM;
    const shouldHaveValidEnumOptions =
      !isEnumSchemaType ||
      (isEnumSchemaType && !!enumOptions?.find(conf => conf.option === data?.[key]));
    const isTargetScope = scope === targetScope;
    const isTargetListingType = isFieldForListingType(targetListingType, fieldConfig);
    const isTargetCategory = isFieldForCategory(targetCategoryIds, fieldConfig);

    if (
      isKnownSchemaType &&
      isTargetScope &&
      isTargetListingType &&
      isTargetCategory &&
      shouldHaveValidEnumOptions
    ) {
      const fieldValue = data?.[key] || null;
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * If listing represents something else than a bookable listing, we set availability-plan to seats=0.
 * Note: this is a performance improvement since the API is backwards compatible.
 *
 * @param {string} processAlias selected for this listing
 * @returns availabilityPlan without any seats available for the listing
 */
const setNoAvailabilityForUnbookableListings = processAlias => {
  return isBookingProcessAlias(processAlias)
    ? {}
    : {
        availabilityPlan: {
          type: 'availability-plan/time',
          timezone: 'Etc/UTC',
          entries: [
            // Note: "no entries" is the same as seats=0 for every entry.
            // { dayOfWeek: 'mon', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'tue', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'wed', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'thu', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'fri', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'sat', startTime: '00:00', endTime: '00:00', seats: 0 },
            // { dayOfWeek: 'sun', startTime: '00:00', endTime: '00:00', seats: 0 },
          ],
        },
      };
};

/**
 * Get initialValues for the form. This function includes
 * title, description, listingType, transactionProcessAlias, unitType,
 * and those publicData & privateData fields that are configured through
 * config.listing.listingFields.
 *
 * @param {object} props
 * @param {object} existingListingTypeInfo info saved to listing's publicData
 * @param {object} listingTypes app's configured types (presets for listings)
 * @param {object} listingFields those extended data fields that are part of configurations
 * @returns initialValues object for the form
 */
const getInitialValues = (
  props,
  existingListingTypeInfo,
  listingTypes,
  listingFields,
  listingCategories,
  categoryKey,
  isNewURI
) => {
  const { description, title, publicData, privateData, geolocation, createdAt } =
    props?.listing?.attributes || {};
  const {
    listingType,
    nearestBodyOfWater = '',
    location = {},
    instantBooking,
    locationParams,
    amenities,
  } = publicData || {};

  const nestedCategories = pickCategoryFields(publicData, categoryKey, 1, listingCategories);
  const legacyListingLocationParams = geolocation && convertGeolocationToParams(geolocation);
  const updatedLocationParams = locationParams ? locationParams : legacyListingLocationParams;

  // Check if listing is older than the current date
  const isOldListing = createdAt ? new Date(createdAt) < new Date() : false;

  // Determine instantBooking behavior
  const computedInstantBooking = isNewURI
    ? [true]
    : isOldListing
    ? instantBooking ?? [false] // Keep false/null values for old listings
    : instantBooking ?? [true]; // Default new listings to true

  return {
    title,
    description,
    nearestBodyOfWater,
    harbormasterPhone: publicData?.harbormasterPhone || '',
    harbormasterWebsite: publicData?.harbormasterWebsite || '',
    location,
    instantBooking: computedInstantBooking,
    amenities,
    ...updatedLocationParams,
    // Transaction type info: listingType, transactionProcessAlias, unitType
    ...getTransactionInfo(listingTypes, existingListingTypeInfo),
    ...initialValuesForListingFields(
      publicData,
      'public',
      listingType,
      nestedCategories,
      listingFields
    ),
    ...initialValuesForListingFields(
      privateData,
      'private',
      listingType,
      nestedCategories,
      listingFields
    ),
  };
};

const EditListingDetailsPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    onAutosave,
    onListingTypeChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    config,
    isCharityProcess,
    isNewURI,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const { publicData, state } = listing?.attributes || {};
  const listingTypes = config.listing.listingTypes;
  const listingFields = config.listing.listingFields;
  const listingCategories = config.categoryConfiguration.categories;
  const categoryKey = config.categoryConfiguration.key;
  const isPublicMoorage = isPublicMoorageListing(publicData);
  const selectableListingCategories = isPublicMoorage
    ? []
    : omitCategoryById(listingCategories, PUBLIC_MOORAGE_CATEGORY);

  const { hasExistingListingType, existingListingTypeInfo } = hasSetListingType(publicData);
  const hasValidExistingListingType =
    hasExistingListingType &&
    !!listingTypes.find(conf => {
      const listinTypesMatch = conf.listingType === existingListingTypeInfo.listingType;
      const unitTypesMatch = conf.transactionType?.unitType === existingListingTypeInfo.unitType;
      return listinTypesMatch && unitTypesMatch;
    });

  const initialValues = getInitialValues(
    props,
    existingListingTypeInfo,
    listingTypes,
    listingFields,
    listingCategories,
    categoryKey,
    isNewURI
  );

  const noListingTypesSet = listingTypes?.length === 0;
  const hasListingTypesSet = listingTypes?.length > 0;
  const canShowEditListingDetailsForm =
    hasListingTypesSet && (!hasExistingListingType || hasValidExistingListingType);
  const isPublished = listing?.id && state !== LISTING_STATE_DRAFT;

  return (
    <div className={classes}>
      <H3 as="h1">
        {isPublished ? (
          <FormattedMessage
            id="EditListingDetailsPanel.title"
            values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
          />
        ) : (
          <FormattedMessage
            id="EditListingDetailsPanel.createListingTitle"
            values={{ lineBreak: <br /> }}
          />
        )}
      </H3>

      {canShowEditListingDetailsForm ? (
        <EditListingDetailsForm
          className={css.form}
          initialValues={initialValues}
          saveActionMsg={submitButtonText}
          onSubmit={(values, submitOptions = {}) => {
            const {
              title,
              description,
              listingType,
              transactionProcessAlias,
              unitType,
              nearestBodyOfWater,
              harbormasterPhone,
              harbormasterWebsite,
              location,
              instantBooking,
              locationType,
              latitudeDD,
              longitudeDD,
              latitudeDegrees,
              longitudeDegrees,
              longitudeDegreesDMS,
              latitudeDegreesDMS,
              latitudeDirection,
              longitudeDirection,
              latitudeDirectionDMS,
              longitudeDirectionDMS,
              longitudeMinutes,
              longitudeMinutesDMS,
              longitudeSecondsDMS,
              latitudeMinutes,
              latitudeMinutesDMS,
              latitudeSecondsDMS,
              amenities,
              ...rest
            } = values;

            const locationParams = {
              longitude: location?.longitude,
              latitude: location?.latitude,
              locationType,
              latitudeDD,
              longitudeDD,
              latitudeDegrees,
              longitudeDegrees,
              longitudeDegreesDMS,
              latitudeDegreesDMS,
              latitudeDirection,
              longitudeDirection,
              latitudeDirectionDMS,
              longitudeDirectionDMS,
              longitudeMinutes,
              longitudeMinutesDMS,
              longitudeSecondsDMS,
              latitudeMinutes,
              latitudeMinutesDMS,
              latitudeSecondsDMS,
            };
            const existingCategories = pickCategoryFields(
              publicData || {},
              categoryKey,
              1,
              listingCategories
            );
            const nestedCategories = isPublicMoorage
              ? existingCategories
              : pickCategoryFields(rest, categoryKey, 1, listingCategories);
            // Public moorages are admin-only; keep their category locked even if users edit other fields.
            const cleanedNestedCategories = isPublicMoorage
              ? existingCategories
              : {
                  ...[1, 2, 3].reduce((a, i) => ({ ...a, [`${categoryKey}${i}`]: null }), {}),
                  ...nestedCategories,
                };
            const publicListingFields = pickListingFieldsData(
              rest,
              'public',
              listingType,
              nestedCategories,
              listingFields
            );
            const privateListingFields = pickListingFieldsData(
              rest,
              'private',
              listingType,
              nestedCategories,
              listingFields
            );
            const harbormasterPublicData = isPublicMoorage
              ? {
                  harbormasterPhone: harbormasterPhone?.trim() || null,
                  harbormasterWebsite: normalizeHarbormasterWebsite(harbormasterWebsite),
                }
              : {};

            const isBuoy = values?.pub_category === MOORING_BUOY;
            // const getPrice = getBoatPrice(listing, isBuoy);

            // New values for listing attributes
            const updateValues = {
              title: title.trim(),
              description,
              geolocation: new LatLng(location?.latitude, location?.longitude),
              publicData: {
                ...(publicData || {}),
                listingType,
                instantBooking,
                transactionProcessAlias: isCharityProcess
                  ? BOOKING_PROCESSES.CHARITY_BOOKING_PROCESS_ALIAS
                  : transactionProcessAlias,
                unitType,
                nearestBodyOfWater,
                ...harbormasterPublicData,
                location,
                locationParams,
                amenities,
                ...cleanedNestedCategories,
                ...publicListingFields,
              },
              privateData: privateListingFields,
              ...setNoAvailabilityForUnbookableListings(transactionProcessAlias),
              ...setNoAvailabilityForUnbookableListings(transactionProcessAlias),
            };

            return (submitOptions.isAutosave ? onAutosave : onSubmit)(updateValues);
          }}
          autosaveEnabled={!!onAutosave}
          selectableListingTypes={listingTypes.map(conf => getTransactionInfo([conf], {}, true))}
          hasExistingListingType={hasExistingListingType}
          selectableCategories={selectableListingCategories}
          pickSelectedCategories={values =>
            pickCategoryFields(values, categoryKey, 1, listingCategories)
          }
          categoryPrefix={categoryKey}
          onListingTypeChange={onListingTypeChange}
          listingFieldsConfig={listingFields}
          marketplaceCurrency={config.currency}
          marketplaceName={config.marketplaceName}
          disabled={disabled}
          ready={ready}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          fetchErrors={errors}
          isNewListingFlow={isNewURI}
          isPublicMoorage={isPublicMoorage}
          autoFocus
        />
      ) : (
        <ErrorMessage
          marketplaceName={config.marketplaceName}
          noListingTypesSet={noListingTypesSet}
          invalidExistingListingType={!hasValidExistingListingType}
        />
      )}
    </div>
  );
};

export default EditListingDetailsPanel;
